import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { getAllPartenaires } from "../../services/partenairesServices";
import SectionTitle from "../../components/ui/SectionTitle";

import { FiChevronLeft, FiChevronRight, FiExternalLink } from "react-icons/fi";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";

const FALLBACK = "/img/no-image.jpg";

function cn(...c) {
  return c.filter(Boolean).join(" ");
}

// ✅ rend les /uploads/... absolues vers l’API, laisse passer https://, /img/...
function toAbsolute(u) {
  if (!u) return "";
  const s = String(u).trim();
  if (/^https?:\/\//i.test(s) || s.startsWith("data:") || s.startsWith("blob:"))
    return s;
  if (!s.startsWith("/uploads")) return s; // ex: /img/...
  const base = process.env.REACT_APP_API_URL?.replace(/\/$/, "") || "";
  return `${base}${s}`;
}

function PartnerCard({ partenaire, onImgError }) {
  const src = toAbsolute(partenaire.photo_url) || FALLBACK;
  const isInternal = Boolean(partenaire.is_detail);
  const isExternal = !isInternal && Boolean(partenaire.link_url);

  const content = (
    <div
      className={cn(
        "group relative h-full overflow-hidden rounded-3xl",
        "border border-slate-200/70 dark:border-slate-800",
        "bg-white/80 dark:bg-slate-900/60 backdrop-blur",
        "shadow-sm transition duration-300",
        "hover:shadow-lg hover:-translate-y-[2px]",
        "focus-within:shadow-lg focus-within:-translate-y-[2px]",
      )}
    >
      {/* Accent bas (léger, premium) */}
      <div
        aria-hidden
        className="h-1.5 w-full opacity-70"
        style={{
          background:
            "linear-gradient(to right, transparent, var(--tw-color-primary, #2563eb), transparent)",
        }}
      />

      {/* Badge type (optionnel, discret) */}
      {(isExternal || isInternal) && (
        <div
          className={cn(
            "absolute right-4 top-4 z-10",
            "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-bold",
            "bg-white/85 dark:bg-slate-950/60 backdrop-blur",
            "border border-slate-200/70 dark:border-slate-800",
            "text-slate-800 dark:text-slate-100",
          )}
        >
          {isExternal ? (
            <>
              <FiExternalLink />
              Site
            </>
          ) : (
            "Détails"
          )}
        </div>
      )}

      {/* Logo */}
      <div className="px-6 pt-6">
        <div
          className={cn(
            "relative rounded-2xl",
            "bg-slate-50 dark:bg-slate-950/30",
            "border border-slate-200/60 dark:border-slate-800/60",
            "grid place-items-center overflow-hidden",
            "h-28 sm:h-32",
            "transition",
            "group-hover:border-primary/40",
          )}
        >
          <img
            src={src}
            alt={partenaire.nom}
            loading="lazy"
            decoding="async"
            onError={onImgError}
            className={cn(
              "max-h-20 sm:max-h-24 w-auto object-contain",
              "transition duration-300",
              "group-hover:scale-[1.03]",
            )}
          />
          {/* glow discret */}
          <span
            aria-hidden
            className="pointer-events-none absolute -inset-10 opacity-0 group-hover:opacity-100 transition"
            style={{
              background:
                "radial-gradient(circle at 50% 60%, rgba(59,130,246,.18), transparent 55%)",
            }}
          />
        </div>
      </div>

      {/* Nom */}
      <div className="px-6 pb-6 pt-4">
        <h3
          className={cn(
            "text-center text-sm sm:text-base font-extrabold tracking-tight",
            "text-slate-900 dark:text-slate-50",
            "line-clamp-2",
          )}
        >
          {partenaire.nom}
        </h3>

        {/* Hint hover */}
        <p className="mt-2 text-center text-xs text-slate-500 dark:text-slate-400">
          {isInternal ? "Voir la fiche partenaire" : isExternal ? "Ouvrir le site" : ""}
        </p>
      </div>
    </div>
  );

  if (isInternal) {
    return (
      <Link
        to={`/partenaires/${encodeURIComponent(partenaire.nom)}`}
        className="block focus:outline-none"
        aria-label={`Voir le partenaire ${partenaire.nom}`}
      >
        {content}
      </Link>
    );
  }

  if (isExternal) {
    return (
      <a
        href={partenaire.link_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block focus:outline-none"
        aria-label={`Ouvrir le site du partenaire ${partenaire.nom}`}
      >
        {content}
      </a>
    );
  }

  // fallback si ni détail ni url (rare)
  return <div className="block">{content}</div>;
}

export default function PartenairesCarousel() {
  const [partenaires, setPartenaires] = useState([]);
  const sliderRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const all = await getAllPartenaires();
        const active = (all || []).filter((p) => p.is_active);
        setPartenaires(active);
      } catch (error) {
        console.error("Erreur lors de la récupération des partenaires", error);
        setPartenaires([]);
      }
    })();
  }, []);

  const onImgError = (e) => {
    if (e?.currentTarget) e.currentTarget.src = FALLBACK;
  };

    const count = partenaires.length;

  // ✅ évite slidesToShow = 0 (même si on return null après)
  const safeCount = Math.max(1, count);

  // Slides visibles max selon device
  const baseShow = Math.min(4, safeCount);
  const mdShow = Math.min(3, safeCount);
  const smShow = Math.min(2, safeCount);
  const xsShow = 1;

  const canLoop = count > baseShow;
  const canAutoPlay = count > baseShow;

  const settings = useMemo(
    () => ({
      infinite: canLoop,
      slidesToShow: baseShow,
      slidesToScroll: 1,
      autoplay: canAutoPlay,
      speed: 900,
      autoplaySpeed: 2800,
      cssEase: "ease-in-out",
      arrows: false,
      dots: count > baseShow,
      pauseOnHover: true,
      pauseOnFocus: true,
      swipeToSlide: true,
      responsive: [
        { breakpoint: 1024, settings: { slidesToShow: mdShow } },
        { breakpoint: 640, settings: { slidesToShow: smShow } },
        { breakpoint: 480, settings: { slidesToShow: xsShow } },
      ],
    }),
    [canLoop, canAutoPlay, baseShow, mdShow, smShow, xsShow, count],
  );

  // ✅ seulement maintenant : return conditionnel
  if (!count) return null;


  return (
    <section
      id="partenaires"
      className="py-12 sm:py-16 bg-lightBackground dark:bg-darkBackground text-black dark:text-white"
    >
      {/* styles slick minimaux (sans fichier CSS custom) */}
      <style>{`
        .slick-dots { bottom: -34px; }
        .slick-dots li button:before { font-size: 10px; opacity: .25; }
        .slick-dots li.slick-active button:before { opacity: .65; }
        @media (prefers-reduced-motion: reduce) {
          .slick-track { transition: none !important; }
        }
      `}</style>

      <div className="mx-auto max-w-7xl px-4">
        <SectionTitle
          title="Nos Partenaires"
          subtitle="Ils soutiennent le club — merci à eux."
        />

        {/* Barre actions (prev/next) */}
        {count > 1 && (
          <div className="mt-6 flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => sliderRef.current?.slickPrev?.()}
              className={cn(
                "h-10 w-10 rounded-full grid place-items-center",
                "bg-white/80 dark:bg-slate-950/40 backdrop-blur",
                "border border-slate-200/70 dark:border-slate-800",
                "text-slate-800 dark:text-slate-100",
                "shadow-sm hover:shadow transition",
              )}
              aria-label="Précédent"
              title="Précédent"
            >
              <FiChevronLeft className="text-xl" />
            </button>

            <button
              type="button"
              onClick={() => sliderRef.current?.slickNext?.()}
              className={cn(
                "h-10 w-10 rounded-full grid place-items-center",
                "bg-white/80 dark:bg-slate-950/40 backdrop-blur",
                "border border-slate-200/70 dark:border-slate-800",
                "text-slate-800 dark:text-slate-100",
                "shadow-sm hover:shadow transition",
              )}
              aria-label="Suivant"
              title="Suivant"
            >
              <FiChevronRight className="text-xl" />
            </button>
          </div>
        )}

        <div className="mt-6">
          {/* Cas 1 seul partenaire => pas de slider */}
          {count === 1 ? (
            <div className="max-w-sm mx-auto">
              <PartnerCard partenaire={partenaires[0]} onImgError={onImgError} />
            </div>
          ) : (
            <Slider ref={sliderRef} {...settings}>
              {partenaires.map((p) => (
                <div key={p.id} className="px-2 py-2">
                  <PartnerCard partenaire={p} onImgError={onImgError} />
                </div>
              ))}
            </Slider>
          )}
        </div>
      </div>
    </section>
  );
}
